/**
 * 설정 > 메뉴 관리 — 목 트리·조작 유틸.
 * Figma「18 메뉴 설정 관리」(13079:3589).
 * flat 트리 + DnD projection으로 순서·상위(대분류↔하위)를 함께 갱신한다.
 */
import { ROUTE_PATHS } from '@/shared/constants/routes';
import {
  Activity,
  Archive,
  BadgeCheck,
  Banknote,
  BarChart3,
  Bell,
  BookOpen,
  Boxes,
  Briefcase,
  Building2,
  Calculator,
  Calendar,
  ChartColumn,
  CircleDollarSign,
  ClipboardList,
  Clock,
  Cloud,
  Coins,
  CreditCard,
  Cross,
  Database,
  Factory,
  FileSpreadsheet,
  FileText,
  Files,
  Filter,
  Flag,
  FlaskConical,
  FolderOpen,
  Globe,
  Handshake,
  HeartPulse,
  HelpCircle,
  Home,
  Hospital,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  LineChart,
  Link2,
  ListChecks,
  Lock,
  Mail,
  MapPin,
  Megaphone,
  Menu,
  MessageSquare,
  Newspaper,
  Package,
  PieChart,
  Pill,
  Receipt,
  ScrollText,
  Search,
  Server,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Star,
  Stethoscope,
  Store,
  Syringe,
  Tag,
  Truck,
  User,
  UserCheck,
  UserCog,
  UserRound,
  Users,
  Wallet,
  Warehouse,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

export type MenuAudience = 'admin' | 'general';

/**
 * 메뉴 아이콘 후보 (lucide) — API 전달용 문자열 키.
 * 사이드바·권한 설정과 동일 키로 해석한다.
 */
export const MENU_ICON_OPTIONS = [
  // 네비·레이아웃
  { key: 'LayoutDashboard', label: '대시보드', Icon: LayoutDashboard },
  { key: 'Home', label: '홈', Icon: Home },
  { key: 'LayoutGrid', label: '그리드', Icon: LayoutGrid },
  { key: 'Menu', label: '메뉴', Icon: Menu },
  // 기초·기관
  { key: 'Database', label: '기초정보', Icon: Database },
  { key: 'Building2', label: '기관', Icon: Building2 },
  { key: 'Hospital', label: '병원', Icon: Hospital },
  { key: 'Store', label: '약국', Icon: Store },
  { key: 'Factory', label: '공장', Icon: Factory },
  { key: 'Warehouse', label: '창고', Icon: Warehouse },
  { key: 'Landmark', label: '기관(공공)', Icon: Landmark },
  // 의료·약품
  { key: 'Pill', label: '의약품', Icon: Pill },
  { key: 'Stethoscope', label: '진료', Icon: Stethoscope },
  { key: 'HeartPulse', label: '바이탈', Icon: HeartPulse },
  { key: 'Activity', label: '모니터링', Icon: Activity },
  { key: 'Syringe', label: '주사', Icon: Syringe },
  { key: 'FlaskConical', label: '실험', Icon: FlaskConical },
  { key: 'Cross', label: '의료십자', Icon: Cross },
  // 사용자·권한
  { key: 'Users', label: '사용자', Icon: Users },
  { key: 'User', label: '사용자(단)', Icon: User },
  { key: 'UserRound', label: '내 계정', Icon: UserRound },
  { key: 'UserCog', label: '계정 설정', Icon: UserCog },
  { key: 'UserCheck', label: '승인 사용자', Icon: UserCheck },
  { key: 'Shield', label: '보안', Icon: Shield },
  { key: 'ShieldCheck', label: '권한', Icon: ShieldCheck },
  { key: 'Lock', label: '잠금', Icon: Lock },
  { key: 'KeyRound', label: '키', Icon: KeyRound },
  { key: 'BadgeCheck', label: '인증', Icon: BadgeCheck },
  // 거래·계약·문서
  { key: 'Handshake', label: '거래처', Icon: Handshake },
  { key: 'Briefcase', label: '업무', Icon: Briefcase },
  { key: 'ScrollText', label: '계약', Icon: ScrollText },
  { key: 'FileText', label: '문서', Icon: FileText },
  { key: 'Files', label: '문서함', Icon: Files },
  { key: 'FolderOpen', label: '폴더', Icon: FolderOpen },
  { key: 'Archive', label: '보관', Icon: Archive },
  { key: 'BookOpen', label: '매뉴얼', Icon: BookOpen },
  { key: 'Newspaper', label: '소식', Icon: Newspaper },
  { key: 'ClipboardList', label: '감사 로그', Icon: ClipboardList },
  { key: 'ListChecks', label: '체크리스트', Icon: ListChecks },
  { key: 'FileSpreadsheet', label: '스프레드시트', Icon: FileSpreadsheet },
  // 정산·재무
  { key: 'Banknote', label: '수수료', Icon: Banknote },
  { key: 'Receipt', label: '세금계산서', Icon: Receipt },
  { key: 'Wallet', label: '지갑', Icon: Wallet },
  { key: 'CreditCard', label: '카드', Icon: CreditCard },
  { key: 'Coins', label: '코인', Icon: Coins },
  { key: 'CircleDollarSign', label: '금액', Icon: CircleDollarSign },
  { key: 'Calculator', label: '계산', Icon: Calculator },
  { key: 'ChartColumn', label: '막대차트', Icon: ChartColumn },
  { key: 'BarChart3', label: '통계', Icon: BarChart3 },
  { key: 'LineChart', label: '추이', Icon: LineChart },
  { key: 'PieChart', label: '비중', Icon: PieChart },
  // 물류·재고
  { key: 'Package', label: '패키지', Icon: Package },
  { key: 'Boxes', label: '박스', Icon: Boxes },
  { key: 'Truck', label: '배송', Icon: Truck },
  { key: 'Tag', label: '태그', Icon: Tag },
  // 커뮤니케이션·일정
  { key: 'Megaphone', label: '공지', Icon: Megaphone },
  { key: 'Bell', label: '알림', Icon: Bell },
  { key: 'Mail', label: '메일', Icon: Mail },
  { key: 'MessageSquare', label: '메시지', Icon: MessageSquare },
  { key: 'Calendar', label: '일정', Icon: Calendar },
  { key: 'Clock', label: '시간', Icon: Clock },
  { key: 'MapPin', label: '위치', Icon: MapPin },
  { key: 'HelpCircle', label: '도움말', Icon: HelpCircle },
  // 시스템·유틸
  { key: 'Settings', label: '설정', Icon: Settings },
  { key: 'SlidersHorizontal', label: '세부 설정', Icon: SlidersHorizontal },
  { key: 'Wrench', label: '도구', Icon: Wrench },
  { key: 'Search', label: '검색', Icon: Search },
  { key: 'Filter', label: '필터', Icon: Filter },
  { key: 'Link2', label: '링크', Icon: Link2 },
  { key: 'Globe', label: '웹', Icon: Globe },
  { key: 'Cloud', label: '클라우드', Icon: Cloud },
  { key: 'Server', label: '서버', Icon: Server },
  { key: 'Sparkles', label: '맥스매치', Icon: Sparkles },
  { key: 'Star', label: '즐겨찾기', Icon: Star },
  { key: 'Flag', label: '플래그', Icon: Flag },
] as const;

export type MenuIconKey = (typeof MENU_ICON_OPTIONS)[number]['key'];

const MENU_ICON_MAP: Record<string, LucideIcon> = Object.fromEntries(
  MENU_ICON_OPTIONS.map((opt) => [opt.key, opt.Icon]),
);

export const DEFAULT_MENU_ICON_KEY: MenuIconKey = 'Menu';

export const resolveMenuIcon = (iconKey?: string | null): LucideIcon =>
  (iconKey && MENU_ICON_MAP[iconKey]) || Menu;

export const isMenuIconKey = (value: string): value is MenuIconKey =>
  value in MENU_ICON_MAP;

/** 연결 경로 없음 (대분류·그룹) — Select 빈 값 대체 */
export const MENU_URL_NONE = '__none__';

/**
 * 앱 ROUTE_PATHS 중 메뉴에 연결 가능한 정적 경로.
 * `$param` 동적 상세·로그인/시연 전용 URL은 제외한다.
 */
const MENU_URL_LABELS: Record<string, string> = {
  [ROUTE_PATHS.DASHBOARD]: '대시보드',
  [ROUTE_PATHS.INSTITUTIONS]: '병의원',
  [ROUTE_PATHS.PHARMACIES]: '문전약국',
  [ROUTE_PATHS.PHARMA_COMPANIES]: '제약사',
  [ROUTE_PATHS.PHARMA_COMPANY_NEW]: '제약사 등록',
  [ROUTE_PATHS.DRUGS]: '의약품',
  [ROUTE_PATHS.DRUG_DATA_CREATE]: '의약품 데이터 생성',
  [ROUTE_PATHS.SETTLEMENT_FEES]: '정산 수수료',
  [ROUTE_PATHS.COMMISSION_AGGREGATOR]: '수수료 집계',
  [ROUTE_PATHS.USERS]: '유저 관리',
  [ROUTE_PATHS.PARTNER_COMPANY]: '업체 관리',
  [ROUTE_PATHS.PARTNER_COMPANY_NEW]: '업체 등록',
  [ROUTE_PATHS.CLIENTS]: '거래처',
  [ROUTE_PATHS.PRESCRIPTION_REVIEW]: '처방 검수',
  [ROUTE_PATHS.PRESCRIPTION_HISTORY]: '확정 처방',
  [ROUTE_PATHS.SETTLEMENT_UPLOAD]: '제약사 정산 정보',
  [ROUTE_PATHS.SETTLEMENT_PRESCRIPTIONS]: '정산 내역 관리',
  [ROUTE_PATHS.SETTLEMENT_GENERATOR]: '정산 시트 생성기',
  [ROUTE_PATHS.REVERSE_TAX]: '세금계산서 역발행',
  [ROUTE_PATHS.BOARD_FAQ]: 'FAQ',
  [ROUTE_PATHS.BOARD_ANNOUNCEMENTS]: '공지사항',
  [ROUTE_PATHS.BOARD_ANNOUNCEMENT_NEW]: '공지 작성',
  [ROUTE_PATHS.TERMS]: '약관 관리',
  [ROUTE_PATHS.CONTRACT_APPENDIX]: '계약 별첨 관리',
  [ROUTE_PATHS.BASE_FEE_RATES]: '기본 수수료율',
  [ROUTE_PATHS.BASE_FEE_RATE_SETTING]: '기본 수수료율 설정',
  [ROUTE_PATHS.DETAIL_FEE_RATES]: '상세 수수료율',
  [ROUTE_PATHS.DETAIL_FEE_RATE_SETTING]: '상세 수수료율 설정',
  [ROUTE_PATHS.MAX_MATCH]: '맥스매치 Standard',
  [ROUTE_PATHS.MAX_MATCH_FULL]: '맥스매치 Full',
  [ROUTE_PATHS.MAX_MATCH_MINI]: '맥스매치 Lite',
  [ROUTE_PATHS.MAX_MATCH_OPS]: '맥스매치 운영',
  [ROUTE_PATHS.MAX_MATCH_OPERATING_HOURS]: '맥스매치 운영 시간',
  [ROUTE_PATHS.MY_ACCOUNT]: '내 프로필',
  [ROUTE_PATHS.SETTINGS_ACCOUNTS]: '계정 관리',
  [ROUTE_PATHS.SETTINGS_AUDIT_LOG]: '감사로그',
  [ROUTE_PATHS.SETTINGS_MENUS]: '메뉴 관리',
};

const MENU_URL_EXCLUDE = new Set<string>([
  ROUTE_PATHS.LOGIN,
  // 약관 버전 등록 — 목록(TERMS) 권한을 상속하므로 메뉴로 따로 연결하지 않는다
  ROUTE_PATHS.TERMS_NEW,
  ROUTE_PATHS.REGISTER,
  ROUTE_PATHS.FORCE_CHANGE_PASSWORD,
  ROUTE_PATHS.ADMIN,
  // 설정 베이스 — 단독 페이지 없음 (하위 profile/accounts 등만 연결)
  ROUTE_PATHS.SETTINGS,
  ROUTE_PATHS.DEMO,
  ROUTE_PATHS.DEMO_HOME,
  ROUTE_PATHS.DEMO_POST_PROCESS,
  ROUTE_PATHS.DEMO_BATCH_JOBS,
  ROUTE_PATHS.DEMO_BATCH_JOB_DETAIL,
]);

export type MenuUrlOption = {
  value: string;
  label: string;
  /** 앱 ROUTE_PATHS에 없는 현재 값 — 수정 유도용으로만 Select에 노출 */
  unmapped?: boolean;
};

export const MENU_URL_OPTIONS: MenuUrlOption[] = [
  { value: MENU_URL_NONE, label: '없음 (그룹/대분류)' },
  ...Object.values(ROUTE_PATHS)
    .filter(
      (path) =>
        typeof path === 'string' &&
        !path.includes('$') &&
        !MENU_URL_EXCLUDE.has(path),
    )
    // enum 중복 값(DEMO === DEMO_HOME) 제거
    .filter((path, index, all) => all.indexOf(path) === index)
    .map((path) => ({
      value: path,
      label: MENU_URL_LABELS[path]
        ? `${MENU_URL_LABELS[path]} · ${path}`
        : path,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'ko')),
];

/** 생성·수정 Select에 쓰는 앱 정의 경로 (「없음」 제외) */
export const KNOWN_MENU_URLS = new Set(
  MENU_URL_OPTIONS.filter((o) => o.value !== MENU_URL_NONE).map((o) => o.value),
);

/** 빈 경로(그룹) 또는 앱 ROUTE_PATHS에 등록된 경로 */
export const isKnownMenuUrl = (url?: string | null): boolean => {
  const trimmed = url?.trim() ?? '';
  if (!trimmed) return true;
  return KNOWN_MENU_URLS.has(trimmed);
};

/**
 * 경로가 있는데 프론트 ROUTE_PATHS에 없음 → 사이드바 아이콘·이동 매핑 실패.
 * 메뉴 관리에서 「경로 수정」 대상으로 표시한다.
 */
export const isUnmappedMenuUrl = (url?: string | null): boolean => {
  const trimmed = url?.trim() ?? '';
  if (!trimmed) return false;
  return !KNOWN_MENU_URLS.has(trimmed);
};

export const listUnmappedMenuItems = <
  T extends { id: string; name: string; url: string },
>(
  items: T[],
): T[] => items.filter((item) => isUnmappedMenuUrl(item.url));

/** 현재 URL이 앱 미등록이면 수정 필요 항목을 앞에 붙인다 (선택지만, 신규 생성엔 미사용) */
export const listMenuUrlOptions = (currentUrl?: string): MenuUrlOption[] => {
  const url = currentUrl?.trim() ?? '';
  if (!url || !isUnmappedMenuUrl(url)) {
    return MENU_URL_OPTIONS;
  }
  return [
    {
      value: url,
      label: `수정 필요 · ${url}`,
      unmapped: true,
    },
    ...MENU_URL_OPTIONS,
  ];
};

/**
 * 이미 메뉴에 연결된 경로를 제외한 선택지.
 * - 기본: 프론트 정의 ROUTE_PATHS만
 * - currentUrl이 미매칭이면 「수정 필요」로만 유지(저장 전 교체 유도)
 */
export const listAvailableMenuUrlOptions = (
  usedUrls: Iterable<string>,
  currentUrl?: string,
): MenuUrlOption[] => {
  const current = currentUrl?.trim() ?? '';
  const used = new Set(
    [...usedUrls].map((u) => u.trim()).filter((u) => u.length > 0),
  );
  if (current) used.delete(current);

  const available = MENU_URL_OPTIONS.filter(
    (opt) => opt.value === MENU_URL_NONE || !used.has(opt.value),
  );

  if (current && isUnmappedMenuUrl(current)) {
    return [
      {
        value: current,
        label: `수정 필요 · ${current}`,
        unmapped: true,
      },
      ...available,
    ];
  }
  return available;
};

/**
 * 메뉴 트리에서 이미 점유된 연결 경로(urlPath).
 * menuKey는 서버 난수라 경로 판별에 쓰지 않는다.
 */
export const collectUsedMenuUrls = (
  items: Array<{ url: string }>,
): string[] => {
  const used = new Set<string>();
  for (const item of items) {
    const url = item.url?.trim();
    if (url) used.add(url);
  }
  return [...used];
};

/** Select value ↔ 저장용 url (없음 = 빈 문자열 → API null) */
export const toMenuUrlSelectValue = (url: string): string => {
  const trimmed = url.trim();
  return trimmed.length > 0 ? trimmed : MENU_URL_NONE;
};

export const fromMenuUrlSelectValue = (value: string): string =>
  value === MENU_URL_NONE ? '' : value;

export type MenuTreeItem = {
  id: string;
  name: string;
  /** 시스템 내부 식별자 — 생성 후 변경 불가 */
  menuKey: string;
  url: string;
  parentId: string | null;
  visible: boolean;
  audience: MenuAudience;
  /** lucide 아이콘 키 — 사이드바 표시용(로컬 UI, API 미전송) */
  iconKey: MenuIconKey;
  /** 상세 헤더 배지 (권한 그룹 등) */
  sectionLabel?: string;
  /** 서버 systemDefined — true면 삭제 불가 */
  systemDefined?: boolean;
  /** 서버 hasChildren 스냅샷 — 없으면 트리에서 추론 */
  hasChildrenFlag?: boolean;
  /** 상세 조회 parentMenuName */
  parentMenuName?: string | null;
};

export type FlattenedMenuItem = MenuTreeItem & {
  depth: number;
};

/** DnD 의도 — UI 힌트·인디케이터용 */
export type MenuDragIntent =
  | 'reorder'
  | 'nest'
  | 'promote'
  | 'blocked-nest';

export type MenuProjection = {
  depth: number;
  parentId: string | null;
  intent: MenuDragIntent;
  /** 안쪽에 드롭될 때 하이라이트할 상위 메뉴 */
  nestTargetId: string | null;
  message: string;
};

/** 들여쓰기 1단 ≈ 하위 메뉴로 편입 */
export const INDENT_WIDTH = 24;
/** 이 이상 오른쪽으로 밀면 하위 편입 의사가 있는 것으로 본다 */
export const NEST_OFFSET_THRESHOLD = 12;
/** 이 이상 왼쪽으로 밀면 최상위 승격 의사 */
export const PROMOTE_OFFSET_THRESHOLD = -12;
/** 사이드바와 동일하게 대분류(0) / 하위(1) — 항상 2단계까지만 */
export const MAX_MENU_DEPTH = 1;

export const ROOT_PARENT_VALUE = '__root__';

export const hasChildMenus = (
  items: MenuTreeItem[],
  id: string,
): boolean => items.some((m) => m.parentId === id);

/** 자손이 없고, 대상이 대분류일 때만 하위로 편입 가능 */
export const canNestUnder = (
  items: MenuTreeItem[],
  activeId: string,
  parentId: string,
): boolean => {
  if (activeId === parentId) return false;
  if (hasChildMenus(items, activeId)) return false;
  if (isDescendantOf(items, activeId, parentId)) return false;
  const parent = items.find((m) => m.id === parentId);
  if (!parent || parent.parentId !== null) return false;
  return true;
};

/** Figma 샘플 트리에 맞춘 초기 메뉴 */
export const createInitialMenuTree = (): MenuTreeItem[] => [
  {
    id: 'basic',
    name: '기본정보',
    menuKey: 'basic_info',
    url: '',
    parentId: null,
    visible: true,
    audience: 'general',
    iconKey: 'Database',
    sectionLabel: '기초정보',
  },
  {
    id: 'users',
    name: '사용자',
    menuKey: 'user_group',
    url: '',
    parentId: null,
    visible: true,
    audience: 'general',
    iconKey: 'Users',
    sectionLabel: '회원 관리',
  },
  {
    id: 'users-mgmt',
    name: '사용자 관리',
    menuKey: 'user_management',
    url: ROUTE_PATHS.USERS,
    parentId: 'users',
    visible: true,
    audience: 'general',
    iconKey: 'Users',
    sectionLabel: '회원 관리',
  },
  {
    id: 'users-contract',
    name: '계약 별첨 관리',
    menuKey: 'contract_appendix',
    url: ROUTE_PATHS.CONTRACT_APPENDIX,
    parentId: 'users',
    visible: true,
    audience: 'general',
    iconKey: 'ScrollText',
    sectionLabel: '회원 관리',
  },
  {
    id: 'users-base-fee',
    name: '기본 수수료율',
    menuKey: 'base_fee_rate',
    url: ROUTE_PATHS.BASE_FEE_RATES,
    parentId: 'users',
    visible: true,
    audience: 'admin',
    iconKey: 'Banknote',
    sectionLabel: '회원 관리',
  },
  {
    id: 'users-detail-fee',
    name: '상세 수수료율',
    menuKey: 'detail_fee_rate',
    url: ROUTE_PATHS.DETAIL_FEE_RATES,
    parentId: 'users',
    visible: true,
    audience: 'admin',
    iconKey: 'Banknote',
    sectionLabel: '회원 관리',
  },
  {
    id: 'clients',
    name: '거래처',
    menuKey: 'clientInfo_management',
    url: ROUTE_PATHS.CLIENTS,
    parentId: null,
    visible: true,
    audience: 'admin',
    iconKey: 'Handshake',
    sectionLabel: '회원 관리',
  },
  {
    id: 'prescriptions',
    name: '처방',
    menuKey: 'prescription_group',
    url: '',
    parentId: null,
    visible: true,
    audience: 'general',
    iconKey: 'FileText',
    sectionLabel: '처방',
  },
  {
    id: 'settlement',
    name: '정산',
    menuKey: 'settlement_group',
    url: '',
    parentId: null,
    visible: true,
    audience: 'admin',
    iconKey: 'Banknote',
    sectionLabel: '정산',
  },
  {
    id: 'maxmatch',
    name: '맥스매치',
    menuKey: 'max_match_group',
    url: '',
    parentId: null,
    visible: false,
    audience: 'general',
    iconKey: 'Sparkles',
    sectionLabel: '맥스매치',
  },
  {
    id: 'board',
    name: '공지/FQA',
    menuKey: 'board_group',
    url: '',
    parentId: null,
    visible: true,
    audience: 'general',
    iconKey: 'Megaphone',
    sectionLabel: '공지/FAQ',
  },
  {
    id: 'settings',
    name: '설정',
    menuKey: 'settings_group',
    url: ROUTE_PATHS.SETTINGS,
    parentId: null,
    visible: true,
    audience: 'admin',
    iconKey: 'Settings',
    sectionLabel: '설정',
  },
];

export const getMenuDepth = (
  items: MenuTreeItem[],
  id: string,
  depth = 0,
): number => {
  const item = items.find((m) => m.id === id);
  if (!item) return depth;
  if (!item.parentId) return depth;
  return getMenuDepth(items, item.parentId, depth + 1);
};

export const flattenMenuItems = (
  items: MenuTreeItem[],
): FlattenedMenuItem[] =>
  items.map((item) => ({
    ...item,
    depth: getMenuDepth(items, item.id),
  }));

/** 자기 자신 + 자손 id (형제 배열 순서 유지) */
export const getSubtreeIds = (
  items: MenuTreeItem[],
  rootId: string,
): string[] => {
  const ids = [rootId];
  for (const child of items.filter((m) => m.parentId === rootId)) {
    ids.push(...getSubtreeIds(items, child.id));
  }
  return ids;
};

export const isDescendantOf = (
  items: MenuTreeItem[],
  ancestorId: string,
  maybeDescendantId: string,
): boolean => {
  if (ancestorId === maybeDescendantId) return true;
  return getSubtreeIds(items, ancestorId).includes(maybeDescendantId);
};

/**
 * 대분류 드래그 중: 해당 하위를 목록에서 숨겨 부모+자식이 한 블록처럼 보이게 한다.
 * (dnd-kit sortable tree의 removeChildrenOf 패턴)
 */
export const listDragVisibleItems = (
  items: MenuTreeItem[],
  activeId: string | null,
): MenuTreeItem[] => {
  if (!activeId) return items;
  const active = items.find((m) => m.id === activeId);
  // 대분류를 잡을 때만 자손을 접는다 (하위 단독 드래그는 형제 정렬)
  if (!active || active.parentId !== null) return items;
  if (!hasChildMenus(items, activeId)) return items;

  const hide = new Set(
    getSubtreeIds(items, activeId).filter((id) => id !== activeId),
  );
  return items.filter((m) => !hide.has(m.id));
};

/** 대분류 블록 드래그 시, over가 하위면 그 소속 대분류 id로 맞춘다 */
export const resolveGroupedRootOverId = (
  items: MenuTreeItem[],
  activeId: string,
  overId: string,
): string => {
  const active = items.find((m) => m.id === activeId);
  if (!active || active.parentId !== null) return overId;
  if (!hasChildMenus(items, activeId)) return overId;

  let cursor = items.find((m) => m.id === overId);
  while (cursor?.parentId) {
    cursor = items.find((m) => m.id === cursor!.parentId);
  }
  return cursor?.id ?? overId;
};

/** 드롭 라인을 행의 위(before) / 아래(after) 중 어디에 그릴지 */
export type MenuDropEdge = 'before' | 'after';

export type MenuDropIndicatorTarget = {
  /** 인디케이터를 붙일 visible 행 id */
  targetId: string;
  edge: MenuDropEdge;
};

/**
 * 단순 순서·블록 이동 시 하이라이트 위치.
 * applyMenuTreeDrag와 같이, 아래로 이동하면 over 서브트리 뒤에 삽입되므로
 * visible 목록에서 그 서브트리의 마지막 행 하단에 그린다.
 */
export const resolveMenuDropIndicator = (
  items: MenuTreeItem[],
  visibleItems: MenuTreeItem[],
  activeId: string,
  overId: string,
): MenuDropIndicatorTarget | null => {
  if (activeId === overId) return null;

  const activeIndex = items.findIndex((m) => m.id === activeId);
  const overIndex = items.findIndex((m) => m.id === overId);
  if (activeIndex < 0 || overIndex < 0) return null;

  // 아래로: over(+자손) 뒤 → 펼쳐진 자식이 있으면 그 마지막 행의 after
  if (overIndex > activeIndex) {
    const subtree = new Set(getSubtreeIds(items, overId));
    let lastVisibleId = overId;
    for (const item of visibleItems) {
      if (item.id === activeId) continue;
      if (subtree.has(item.id)) {
        lastVisibleId = item.id;
      }
    }
    return { targetId: lastVisibleId, edge: 'after' };
  }

  // 위로: over 앞
  return { targetId: overId, edge: 'before' };
};

export const listDirectChildMenus = (
  items: MenuTreeItem[],
  parentId: string,
): MenuTreeItem[] => items.filter((m) => m.parentId === parentId);

const arrayMove = <T,>(array: T[], from: number, to: number): T[] => {
  const next = array.slice();
  const [item] = next.splice(from, 1);
  if (item === undefined) return array;
  next.splice(to, 0, item);
  return next;
};

const resolveParentId = (
  items: FlattenedMenuItem[],
  activeIndex: number,
  depth: number,
): string | null => {
  if (depth === 0) return null;

  const previousItem = items[activeIndex - 1];
  if (!previousItem) return null;

  if (depth === previousItem.depth) {
    return previousItem.parentId;
  }

  if (depth > previousItem.depth) {
    return previousItem.id;
  }

  const ancestor = items
    .slice(0, activeIndex)
    .reverse()
    .find((item) => item.depth === depth);

  return ancestor?.parentId ?? null;
};

const projectionMessage = (
  intent: MenuDragIntent,
  parentName?: string,
): string => {
  switch (intent) {
    case 'nest':
      return parentName
        ? `「${parentName}」의 하위로 이동`
        : '하위로 이동';
    case 'promote':
      return '최상위(대분류)로 승격';
    case 'blocked-nest':
      return '하위가 있는 대분류는 다른 메뉴 아래로 옮길 수 없습니다';
    default:
      return '순서 변경';
  }
};

/**
 * 드래그 수평 오프셋 + over 위치로 예상 depth·parentId·의도를 계산한다.
 * - 오른쪽: 대분류 하위로 편입 (2단계만, 자식 있는 대분류는 blocked)
 * - 왼쪽: 최상위 승격
 * - 그 외: 순서 변경
 */
export const getMenuProjection = (
  items: FlattenedMenuItem[],
  activeId: string,
  overId: string,
  dragOffsetX: number,
): MenuProjection | null => {
  const overIndex = items.findIndex((m) => m.id === overId);
  const activeIndex = items.findIndex((m) => m.id === activeId);
  const activeItem = items[activeIndex];
  const overItem = items[overIndex];
  if (overIndex < 0 || !activeItem || !overItem) return null;

  const activeSubtree = new Set(getSubtreeIds(items, activeId));
  if (activeId !== overId && activeSubtree.has(overId)) return null;

  const hasChildren = hasChildMenus(items, activeId);
  const wantsNest = dragOffsetX >= NEST_OFFSET_THRESHOLD;
  const wantsPromote = dragOffsetX <= PROMOTE_OFFSET_THRESHOLD;

  // 자식 있는 대분류를 하위로 넣으려 할 때 — 순서는 유지하되 의도만 차단 표시
  if (hasChildren && wantsNest && activeItem.depth === 0) {
    const nestCandidate =
      overItem.depth === 0 && overId !== activeId
        ? overId
        : overItem.parentId;
    if (nestCandidate && nestCandidate !== activeId) {
      return {
        depth: 0,
        parentId: null,
        intent: 'blocked-nest',
        nestTargetId: null,
        message: projectionMessage('blocked-nest'),
      };
    }
  }

  // 하위 → 왼쪽: 최상위 승격 (over와 무관하게 오프셋 기준)
  if (!hasChildren && wantsPromote && activeItem.depth > 0) {
    return {
      depth: 0,
      parentId: null,
      intent: 'promote',
      nestTargetId: null,
      message: projectionMessage('promote'),
    };
  }

  // over가 대분류 + 오른쪽 → 그 하위로 편입
  if (
    !hasChildren &&
    wantsNest &&
    activeId !== overId &&
    overItem.depth === 0 &&
    canNestUnder(items, activeId, overId)
  ) {
    return {
      depth: 1,
      parentId: overId,
      intent: 'nest',
      nestTargetId: overId,
      message: projectionMessage('nest', overItem.name),
    };
  }

  // over가 하위 메뉴 + 오른쪽 → 같은 대분류의 형제로 편입
  if (
    !hasChildren &&
    wantsNest &&
    activeId !== overId &&
    overItem.parentId &&
    canNestUnder(items, activeId, overItem.parentId)
  ) {
    const parent = items.find((m) => m.id === overItem.parentId);
    return {
      depth: 1,
      parentId: overItem.parentId,
      intent: 'nest',
      nestTargetId: overItem.parentId,
      message: projectionMessage('nest', parent?.name),
    };
  }

  // sortable tree: active를 over 자리로 옮긴 뒤 previous 기준
  const visual = arrayMove(items, activeIndex, overIndex);
  const visualActiveIndex = visual.findIndex((m) => m.id === activeId);
  const previousItem = visual[visualActiveIndex - 1];

  const maxDepth = previousItem
    ? Math.min(MAX_MENU_DEPTH, previousItem.depth + 1)
    : 0;
  const effectiveMaxDepth = hasChildren ? 0 : maxDepth;

  let depth = activeItem.depth + Math.round(dragOffsetX / INDENT_WIDTH);
  if (depth > effectiveMaxDepth) depth = effectiveMaxDepth;
  if (depth < 0) depth = 0;
  depth = Math.min(MAX_MENU_DEPTH, depth);

  if (
    !hasChildren &&
    wantsNest &&
    previousItem &&
    previousItem.depth === 0 &&
    depth < 1 &&
    canNestUnder(items, activeId, previousItem.id)
  ) {
    depth = 1;
  }

  const parentId = resolveParentId(visual, visualActiveIndex, depth);
  const parentChanged = parentId !== activeItem.parentId;
  let intent: MenuDragIntent = 'reorder';
  if (parentChanged && parentId === null && activeItem.parentId !== null) {
    intent = 'promote';
  } else if (parentChanged && parentId !== null) {
    intent = 'nest';
  }

  return {
    depth,
    parentId,
    intent,
    nestTargetId: intent === 'nest' ? parentId : null,
    message: projectionMessage(
      intent,
      parentId
        ? items.find((m) => m.id === parentId)?.name
        : undefined,
    ),
  };
};

/**
 * DnD 결과를 트리에 반영 — 순서 + parentId(대분류↔하위) 동시 갱신.
 * blocked-nest 는 트리를 바꾸지 않고 projection만 반환한다.
 */
export const applyMenuTreeDrag = (
  items: MenuTreeItem[],
  activeId: string,
  overId: string,
  dragOffsetX = 0,
): { items: MenuTreeItem[]; projection: MenuProjection | null } => {
  const flat = flattenMenuItems(items);
  const projection = getMenuProjection(flat, activeId, overId, dragOffsetX);
  if (!projection) return { items, projection: null };

  if (projection.intent === 'blocked-nest') {
    return { items, projection };
  }

  const active = items.find((m) => m.id === activeId);
  if (!active) return { items, projection };

  const subtreeIds = getSubtreeIds(items, activeId);
  const subtreeSet = new Set(subtreeIds);
  if (activeId !== overId && subtreeSet.has(overId)) {
    return { items, projection };
  }

  const without = items.filter((m) => !subtreeSet.has(m.id));
  const block = items
    .filter((m) => subtreeSet.has(m.id))
    .map((m) =>
      m.id === activeId ? { ...m, parentId: projection.parentId } : { ...m },
    );

  const placeBlock = (insertAt: number) => ({
    items: [
      ...without.slice(0, insertAt),
      ...block,
      ...without.slice(insertAt),
    ],
    projection,
  });

  // 같은 행에서 승격만 (위치 유지)
  if (activeId === overId) {
    const originalIndex = items.findIndex((m) => m.id === activeId);
    const insertAt = items
      .slice(0, originalIndex)
      .filter((m) => !subtreeSet.has(m.id)).length;
    return placeBlock(insertAt);
  }

  const overPos = without.findIndex((m) => m.id === overId);
  if (overPos < 0) {
    return { items: [...without, ...block], projection };
  }

  const activeIndex = items.findIndex((m) => m.id === activeId);
  const overIndex = items.findIndex((m) => m.id === overId);

  // 하위로 편입: 대상 대분류 바로 다음(첫 자식) 또는 형제 over 다음
  if (projection.intent === 'nest' && projection.parentId) {
    if (projection.parentId === overId) {
      return placeBlock(overPos + 1);
    }
    const parentPos = without.findIndex((m) => m.id === projection.parentId);
    if (overPos >= 0 && without[overPos]?.parentId === projection.parentId) {
      const place = overIndex > activeIndex ? overPos + 1 : overPos;
      return placeBlock(place);
    }
    if (parentPos >= 0) {
      let insertAt = parentPos + 1;
      while (
        insertAt < without.length &&
        without[insertAt]?.parentId === projection.parentId
      ) {
        insertAt += 1;
      }
      return placeBlock(insertAt);
    }
  }

  // 승격하며 다른 행으로 이동: over 기준 루트 자리
  if (projection.intent === 'promote') {
    if (overIndex > activeIndex) {
      const overSubtree = new Set(getSubtreeIds(without, overId));
      let insertAt = overPos + 1;
      while (
        insertAt < without.length &&
        overSubtree.has(without[insertAt]!.id)
      ) {
        insertAt += 1;
      }
      return placeBlock(insertAt);
    }
    return placeBlock(overPos);
  }

  // 단순 순서 변경
  if (overIndex > activeIndex) {
    const overSubtree = new Set(getSubtreeIds(without, overId));
    let insertAt = overPos + 1;
    while (insertAt < without.length && overSubtree.has(without[insertAt]!.id)) {
      insertAt += 1;
    }
    return placeBlock(insertAt);
  }

  return placeBlock(overPos);
};

/** @deprecated applyMenuTreeDrag 사용 */
export const moveMenuSubtree = (
  items: MenuTreeItem[],
  activeId: string,
  overId: string,
): MenuTreeItem[] => applyMenuTreeDrag(items, activeId, overId, 0).items;

/**
 * 직전 항목 기준으로 한 단계 하위로 내린다.
 * 이미 하위이거나 자식이 있는 대분류면 null.
 */
export const indentMenuItem = (
  items: MenuTreeItem[],
  id: string,
): MenuTreeItem[] | null => {
  if (hasChildMenus(items, id)) return null;
  const index = items.findIndex((m) => m.id === id);
  const item = items[index];
  if (!item || index <= 0) return null;
  if (item.parentId !== null) return null;

  const prev = items[index - 1];
  if (!prev) return null;
  const newParentId = prev.parentId ?? prev.id;
  if (!canNestUnder(items, id, newParentId)) return null;
  return reparentMenuItem(items, id, newParentId);
};

/**
 * 하위를 최상위 대분류로 올린다. 대분류면 null.
 */
export const outdentMenuItem = (
  items: MenuTreeItem[],
  id: string,
): MenuTreeItem[] | null => {
  const item = items.find((m) => m.id === id);
  if (!item?.parentId) return null;
  return reparentMenuItem(items, id, null);
};

/**
 * 상위 메뉴 변경 (상세 Select).
 * 자손 포함 이동. 자기·자손·MAX_DEPTH 위반은 무시.
 */
export const reparentMenuItem = (
  items: MenuTreeItem[],
  id: string,
  newParentId: string | null,
): MenuTreeItem[] => {
  const item = items.find((m) => m.id === id);
  if (!item) return items;
  if (item.parentId === newParentId) return items;
  if (newParentId && isDescendantOf(items, id, newParentId)) return items;
  if (newParentId !== null && items.some((m) => m.parentId === id)) {
    return items;
  }
  if (newParentId !== null) {
    const parent = items.find((m) => m.id === newParentId);
    if (parent?.parentId) return items;
  }

  const next = items.map((m) =>
    m.id === id ? { ...m, parentId: newParentId } : m,
  );

  const subtreeIds = getSubtreeIds(next, id);
  const subtreeSet = new Set(subtreeIds);
  const without = next.filter((m) => !subtreeSet.has(m.id));
  const block = next.filter((m) => subtreeSet.has(m.id));

  if (newParentId === null) {
    return [...without, ...block];
  }

  const parentIndex = without.findIndex((m) => m.id === newParentId);
  if (parentIndex < 0) return next;

  let insertAt = parentIndex + 1;
  while (
    insertAt < without.length &&
    without[insertAt]?.parentId === newParentId
  ) {
    insertAt += 1;
  }

  return [
    ...without.slice(0, insertAt),
    ...block,
    ...without.slice(insertAt),
  ];
};

export const listParentOptions = (
  items: MenuTreeItem[],
  editingId: string | null,
): { value: string; label: string }[] => {
  const options = [{ value: ROOT_PARENT_VALUE, label: '최상위 (없음)' }];
  const editingHasChildren = editingId
    ? items.some((m) => m.parentId === editingId)
    : false;

  if (editingHasChildren) {
    return options;
  }

  for (const item of items) {
    if (item.parentId !== null) continue;
    if (editingId) {
      if (item.id === editingId) continue;
      if (isDescendantOf(items, editingId, item.id)) continue;
    }
    options.push({ value: item.id, label: item.name });
  }
  return options;
};

/** 선택 중 메뉴 기준 신규 메뉴 기본 상위 */
export const resolveNewMenuParentId = (
  items: MenuTreeItem[],
  selectedId: string | null,
): string | null => {
  if (!selectedId) return null;
  const selected = items.find((m) => m.id === selectedId);
  if (!selected) return null;
  if (selected.parentId === null) return selected.id;
  return selected.parentId;
};

export const createDraftMenuItem = (
  parentId: string | null = null,
): MenuTreeItem => {
  const stamp = Date.now().toString(36);
  return {
    id: `new-${stamp}`,
    name: '새 메뉴',
    menuKey: `menu_${stamp}`,
    url: '',
    parentId,
    visible: true,
    audience: 'general',
    iconKey: DEFAULT_MENU_ICON_KEY,
    sectionLabel: '메뉴',
  };
};

/** 메뉴 식별자(menuKey) — 영문·숫자·_ 만, 권한 menuCode와 맞추기 쉽게 */
export const MENU_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

export type CreateMenuInput = {
  name: string;
  menuKey: string;
  parentId: string | null;
  visible: boolean;
};

export const isMenuKeyAvailable = (
  items: MenuTreeItem[],
  menuKey: string,
  excludeId?: string,
): boolean =>
  !items.some(
    (m) => m.menuKey === menuKey && (excludeId ? m.id !== excludeId : true),
  );

export type AddMenuResult =
  | { ok: true; items: MenuTreeItem[]; created: MenuTreeItem }
  | { ok: false; error: string };

/**
 * 모달에서 확정한 값으로 메뉴를 트리에 추가한다.
 * 식별자 중복·형식·상위(대분류만)를 검증한다.
 */
export const addMenuToTree = (
  items: MenuTreeItem[],
  input: CreateMenuInput,
): AddMenuResult => {
  const name = input.name.trim();
  const menuKey = input.menuKey.trim();

  if (!name) {
    return { ok: false, error: '메뉴명을 입력해주세요' };
  }
  if (!menuKey) {
    return { ok: false, error: '식별자를 입력해주세요' };
  }
  if (!MENU_KEY_PATTERN.test(menuKey)) {
    return {
      ok: false,
      error: '식별자는 영문 소문자로 시작하고, 영문·숫자·_ 만 사용할 수 있습니다',
    };
  }
  if (!isMenuKeyAvailable(items, menuKey)) {
    return { ok: false, error: '이미 사용 중인 식별자입니다' };
  }
  if (input.parentId) {
    const parent = items.find((m) => m.id === input.parentId);
    if (!parent || parent.parentId !== null) {
      return { ok: false, error: '상위 메뉴는 대분류만 선택할 수 있습니다' };
    }
  }

  const stamp = Date.now().toString(36);
  const created: MenuTreeItem = {
    id: `menu-${stamp}`,
    name,
    menuKey,
    url: '',
    parentId: input.parentId,
    visible: input.visible,
    audience: 'general',
    iconKey: DEFAULT_MENU_ICON_KEY,
    sectionLabel: '메뉴',
  };

  return {
    ok: true,
    items: upsertMenuItem(items, created),
    created,
  };
};

export const upsertMenuItem = (
  items: MenuTreeItem[],
  item: MenuTreeItem,
): MenuTreeItem[] => {
  const exists = items.some((m) => m.id === item.id);
  if (!exists) {
    return reparentMenuItem([...items, item], item.id, item.parentId);
  }
  const withFields = items.map((m) => (m.id === item.id ? { ...item } : m));
  const prev = items.find((m) => m.id === item.id);
  if (prev && prev.parentId !== item.parentId) {
    return reparentMenuItem(withFields, item.id, item.parentId);
  }
  return withFields;
};
